import { App } from '@capacitor/app';
import { Injectable, inject } from '@angular/core';

// PermissionState is already in use (global var)
import { PermissionState as CapPermissionState, Capacitor, PluginListenerHandle } from '@capacitor/core';
import {
  LocalNotificationSchema,
  LocalNotifications,
  Schedule,
} from '@capacitor/local-notifications';

import { TranslateService } from '@ngx-translate/core';
import { unbindPluginListeners } from 'src/app/@shared/app-helper';

const CLEAR_TIMEOUT = 30000;
const CONSOLE_TAG = 'LocalNotificationService';

type SupportedPluginListeners = 'appStateChange' |
'localNotificationReceived' |
'localNotificationActionPerformed';

@Injectable({
  providedIn: 'root',
})
export class LocalNotificationService {
  private clearNotificationTimeout: ReturnType<typeof setTimeout>;
  private pluginListeners: { [key in SupportedPluginListeners]?: PluginListenerHandle } = {};
  private isEnabled: boolean; // not true when permission hasn't been granted

  private translate = inject(TranslateService);

  public ngOnDestroy(): void {
    if (this.isEnabled) {
      unbindPluginListeners(this.pluginListeners);
    }
  }

  public async init(): Promise<void> {
    const granted = await this.hasPermissionGranted();

    if (!granted) {
      return Promise.resolve();
    }

    return this.onPermissionGranted();
  }

  public async hasPermissionGranted(): Promise<boolean> {
    let hasPermissionGranted: boolean;

    try {
      const { display } = await LocalNotifications.checkPermissions();

      hasPermissionGranted = display === 'granted';
    } catch (error) {
      console.error(`${CONSOLE_TAG}: hasPermissionGranted`, error);
    }

    return Promise.resolve(hasPermissionGranted);
  }

  public async requestPermissions(): Promise<CapPermissionState> {
    let { display: state } = await LocalNotifications.checkPermissions();

    // request permission if not granted yet
    if (state !== 'granted') {
      const status = await LocalNotifications.requestPermissions();

      state = status.display;
    }

    return Promise.resolve(state);
  }

  public async cancelAllNotifications(): Promise<void> {
    console.info(`${CONSOLE_TAG}: cancelAllLocalNotifications`);

    const { notifications } = await LocalNotifications.getPending();

    await LocalNotifications.cancel({
      notifications,
    });
  }

  private async onPermissionGranted(): Promise<void> {
    try {
      await this.bindPluginListeners();
      await this.scheduleNotifications();
      this.isEnabled = true;
    } catch (error) {
      console.error(`${CONSOLE_TAG}: onPermissionGranted`, error);
    }

    return Promise.resolve();
  }

  private async scheduleNotifications(): Promise<void> {
    await this.scheduleWeeklyNotification();

    return Promise.resolve();
  }

  // basic example for local notification
  private async scheduleWeeklyNotification(): Promise<void> {
    console.info(`${CONSOLE_TAG}: scheduleWeeklyNotification`);
    const at = new Date();

    const DAYS_IN_WEEK = 7;
    const DAY_INDEX = 1; // 0 - sunday, 1 - monday, etc.

    at.setDate(at.getDate() + ((DAY_INDEX + (DAYS_IN_WEEK - at.getDay())) % DAYS_IN_WEEK));
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    at.setHours(9, 0, 0, 0);
    const schedule: Schedule = {
      at,
    };

    const notification: LocalNotificationSchema = {
      body: this.translate.instant('NOTIFICATION.WEEKLY.BODY'),
      id: 1,
      schedule,
      title: this.translate.instant('NOTIFICATION.WEEKLY.TITLE'),
    };

    await LocalNotifications.schedule({
      notifications: [notification],
    });

    return Promise.resolve();
  }

  private clearAllNotifications(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    clearTimeout(this.clearNotificationTimeout);
    this.clearNotificationTimeout = setTimeout(async () => {
      try {
        // This must only happen, after we had a chance to review our received pushes
        console.info(`${CONSOLE_TAG}: clearAllNotifications`);
        await LocalNotifications.removeAllDeliveredNotifications();
      } catch (error) {
        console.info(`${CONSOLE_TAG}: Could not removeAllDeliveredNotifications`, error);
      }
    }, CLEAR_TIMEOUT);
  }

  // TODO: check if we still need ngZone.run() inside our listeners
  private async bindPluginListeners(): Promise<void> {
    this.pluginListeners.appStateChange = await App.addListener('appStateChange', (result) => {
      console.info(`${CONSOLE_TAG}: appStateChange`, result);

      if (!result?.isActive) { return; }

      this.clearAllNotifications();
    });

    this.pluginListeners.localNotificationReceived = await LocalNotifications.addListener('localNotificationReceived', (result) => {
      console.info(`${CONSOLE_TAG}: notificationReceived`, result);
    });

    this.pluginListeners.localNotificationActionPerformed = await LocalNotifications.addListener('localNotificationActionPerformed', (result) => {
      console.info(`${CONSOLE_TAG}: actionPerformed`, result);
    });

    return Promise.resolve();
  }
}
